import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { parseDate, formatDateBR } from "../utils/dateUtils";
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  Clock,
  Users,
  Music,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Send,
  Check,
  Search,
  GripVertical,
  ArrowUp,
  ArrowDown,
  FileText,
  Loader2,
  Bell,
  X,
  ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to check if a schedule is in the past (before today, NOT including today)
const isSchedulePast = (schedule) => {
  if (!schedule.evento_data) return false;
  
  // Get today's date at midnight (local time)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Parse schedule date - handle both YYYY-MM-DD and ISO formats
  const dateStr = schedule.evento_data.split('T')[0]; // Remove time part if present
  const [year, month, day] = dateStr.split('-').map(Number);
  const scheduleDate = new Date(year, month - 1, day); // month is 0-indexed
  scheduleDate.setHours(0, 0, 0, 0);
  
  // Only past if BEFORE today (not including today)
  return scheduleDate.getTime() < today.getTime();
};

export default function SchedulesPage() {
  const { currentBranch, user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [events, setEvents] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [people, setPeople] = useState([]);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [whatsappDialog, setWhatsappDialog] = useState(false);
  const [whatsappLinks, setWhatsappLinks] = useState([]);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [expandedSchedule, setExpandedSchedule] = useState(null);
  
  // Version selector state
  const [versionSelectorOpen, setVersionSelectorOpen] = useState(false);
  const [selectedSongForVersion, setSelectedSongForVersion] = useState(null);
  
  const [form, setForm] = useState({
    nome: "",
    evento_id: "",
    ministerio_id: "",
    ministerios_ids: [], // NOVO: Array para múltiplos ministérios
    pessoas: [],
    musicas: [],
    observacoes: "",
    hora_voluntario_geral: ""
  });

  const [showPeopleSelector, setShowPeopleSelector] = useState(false);
  const [showSongsSelector, setShowSongsSelector] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState("");
  const [songsSearch, setSongsSearch] = useState("");
  const [unavailablePeople, setUnavailablePeople] = useState([]);
  
  // Fullscreen selector modals for mobile
  const [peopleSelectorOpen, setPeopleSelectorOpen] = useState(false);
  const [songsSelectorOpen, setSongsSelectorOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentBranch]);

  const loadData = async () => {
    try {
      const filialParam = currentBranch ? `?filial_id=${currentBranch.id}` : '';
      const [schedulesRes, eventsRes, ministriesRes, functionsRes, peopleRes, songsRes] = await Promise.all([
        axios.get(`${API}/schedules${filialParam}`),
        axios.get(`${API}/events${filialParam}`),
        axios.get(`${API}/ministries${filialParam}`),
        axios.get(`${API}/functions${filialParam}`),
        axios.get(`${API}/people/approved-volunteers${filialParam}`),
        axios.get(`${API}/songs`)
      ]);
      setSchedules(schedulesRes.data);
      setEvents(eventsRes.data);
      setMinistries(ministriesRes.data);
      setFunctions(functionsRes.data);
      setPeople(peopleRes.data);
      setSongs(songsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const getMinistryFunctions = useCallback((ministryId) => {
    return functions.filter(f => 
      f.ministerios_ids?.includes(ministryId) || f.ministerio_id === ministryId
    );
  }, [functions]);

  // Retorna funções de TODOS os ministérios selecionados (sem duplicatas)
  const getMultiMinistryFunctions = useCallback((ministryIds) => {
    if (!ministryIds || ministryIds.length === 0) return [];
    
    const allFunctions = [];
    const seenIds = new Set();
    
    ministryIds.forEach(ministryId => {
      const funcs = functions.filter(f => 
        f.ministerios_ids?.includes(ministryId) || f.ministerio_id === ministryId
      );
      funcs.forEach(func => {
        if (!seenIds.has(func.id)) {
          seenIds.add(func.id);
          allFunctions.push(func);
        }
      });
    });
    
    return allFunctions;
  }, [functions]);

  // Filtrar ministérios que o usuário tem acesso
  const accessibleMinistries = useMemo(() => {
    if (!user) return [];
    
    // Admin vê todos os ministérios
    if (user.nivel_acesso === "admin") {
      return ministries;
    }
    
    // Para outros usuários, filtrar por:
    // 1. É líder do ministério (comparar com pessoa_id, não user.id)
    // 2. Tem status "aprovado" no ministério
    return ministries.filter(ministry => {
      // Verifica se é líder (lideres contém pessoa_id, não user_id)
      const pessoaId = user.pessoa_id;
      if (pessoaId) {
        const isLeader = ministry.lideres?.some(lider => lider.id === pessoaId);
        if (isLeader) return true;
        
        // Também verifica lider_id legado
        if (ministry.lider_id === pessoaId) return true;
      }
      
      // Verifica se tem status aprovado no ministério
      const interesse = user.ministerios_interesse?.find(i => i.ministerio_id === ministry.id);
      if (interesse?.status === "aprovado") return true;
      
      return false;
    });
  }, [ministries, user]);

  // Get people that can serve in a specific function
  // Validates if person is active in the ministry (status = aprovado)
  // AND has the function in funcoes_habilitadas (if configured)
  const getPeopleByFunction = useCallback((functionId, ministryId = null) => {
    return people.filter(p => {
      // Must be approved as volunteer
      if (!p.aprovado_funcao) return false;
      
      // If ministryId provided, check if person is active (status = aprovado) in that ministry
      if (ministryId) {
        const interesse = p.ministerios_interesse?.find(i => i.ministerio_id === ministryId);
        if (!interesse || interesse.status !== "aprovado") {
          return false;
        }
        
        // Check funcoes_habilitadas in ministerios_interesse
        const funcHab = interesse.funcoes_habilitadas;
        
        // If funcoes_habilitadas exists and has values, check if this function is enabled
        // If funcoes_habilitadas is empty/undefined, person can serve any function
        if (funcHab && Array.isArray(funcHab) && funcHab.length > 0) {
          if (!funcHab.includes(functionId)) {
            return false;
          }
        }
        
        return true;
      }
      
      // Fallback to old logic (funcoes_desejadas) if no ministryId
      const hasFunctionInArray = p.funcoes_desejadas?.includes(functionId);
      const hasFunctionSingle = p.funcao_desejada === functionId;
      return hasFunctionInArray || hasFunctionSingle;
    });
  }, [people]);

  // Get all people that belong to the selected ministry (for general assignment)
  // Only returns people with "aprovado" status in the ministry
  const getPeopleByMinistry = useCallback((ministryId) => {
    return people.filter(p => {
      // Must be approved as volunteer
      if (!p.aprovado_funcao) return false;
      
      // Check if person has "aprovado" status in ministerios_interesse for this ministry
      const interesse = p.ministerios_interesse?.find(i => i.ministerio_id === ministryId);
      
      // Only include if status is "aprovado" (active in ministry)
      return interesse?.status === "aprovado";
    });
  }, [people]);

  // Get people from MULTIPLE ministries (sem duplicatas)
  const getPeopleByMultipleMinistries = useCallback((ministryIds) => {
    if (!ministryIds || ministryIds.length === 0) return [];
    
    const seenIds = new Set();
    const result = [];
    
    people.forEach(p => {
      if (!p.aprovado_funcao) return;
      if (seenIds.has(p.id)) return;
      
      // Check if person is approved in ANY of the selected ministries
      const isApprovedInAny = ministryIds.some(ministryId => {
        const interesse = p.ministerios_interesse?.find(i => i.ministerio_id === ministryId);
        return interesse?.status === "aprovado";
      });
      
      if (isApprovedInAny) {
        seenIds.add(p.id);
        result.push(p);
      }
    });
    
    return result;
  }, [people]);

  const isWorshipMinistry = useCallback((ministryId) => {
    const ministry = ministries.find(m => m.id === ministryId);
    return ministry?.tipo_louvor;
  }, [ministries]);

  // Pré-selecionar uma pessoa para cada função quando for ministério de louvor
  const preSelectPeopleForMinistry = useCallback((ministryId, horaGeral = "") => {
    const ministry = ministries.find(m => m.id === ministryId);
    if (!ministry?.tipo_louvor) return [];
    
    const ministryFunctions = getMinistryFunctions(ministryId);
    const preSelectedPeople = [];
    
    ministryFunctions.forEach(func => {
      const peopleForFunction = getPeopleByFunction(func.id, ministryId);
      if (peopleForFunction.length > 0) {
        // Pega a primeira pessoa disponível para cada função
        const person = peopleForFunction[0];
        preSelectedPeople.push({
          person_id: person.id,
          person_nome: person.nome_completo,
          function_id: func.id,
          function_nome: func.nome,
          hora_voluntario: horaGeral
        });
      }
    });
    
    return preSelectedPeople;
  }, [ministries, getMinistryFunctions, getPeopleByFunction]);

  // Handler para quando o ministério é alterado
  const handleMinistryChange = (ministryId) => {
    const preSelected = preSelectPeopleForMinistry(ministryId, form.hora_voluntario_geral);
    setForm(prev => ({ 
      ...prev, 
      ministerio_id: ministryId,
      ministerios_ids: [ministryId], // Mantém compatibilidade
      pessoas: preSelected, 
      musicas: [] 
    }));
    
    if (preSelected.length > 0) {
      toast.success(`${preSelected.length} pessoa(s) pré-selecionada(s)`);
    }
  };

  // Handler para toggle de ministério na seleção múltipla
  const handleMinistryToggle = (ministryId) => {
    setForm(prev => {
      const currentIds = prev.ministerios_ids || [];
      let newIds;
      
      if (currentIds.includes(ministryId)) {
        // Remove
        newIds = currentIds.filter(id => id !== ministryId);
      } else {
        // Adiciona
        newIds = [...currentIds, ministryId];
      }
      
      // Atualiza ministerio_id para o primeiro (para compatibilidade)
      const primaryMinistryId = newIds[0] || "";
      
      return {
        ...prev,
        ministerios_ids: newIds,
        ministerio_id: primaryMinistryId,
        // Limpa pessoas quando muda os ministérios
        pessoas: []
      };
    });
  };

  // Filtrar pessoas pela busca - suporta múltiplos ministérios
  const filteredPeopleByFunction = useMemo(() => {
    const ministryIds = form.ministerios_ids?.length > 0 ? form.ministerios_ids : (form.ministerio_id ? [form.ministerio_id] : []);
    if (ministryIds.length === 0) return {};
    
    const result = {};
    const allFunctions = getMultiMinistryFunctions(ministryIds);
    
    // Get all ACTIVE people from ALL selected ministries
    const allMinistryPeople = getPeopleByMultipleMinistries(ministryIds);
    
    allFunctions.forEach(func => {
      // Get people specifically enabled for this function in ANY of the selected ministries
      let peopleForFunction = [];
      const seenIds = new Set();
      
      ministryIds.forEach(ministryId => {
        const funcPeople = getPeopleByFunction(func.id, ministryId);
        funcPeople.forEach(p => {
          if (!seenIds.has(p.id)) {
            seenIds.add(p.id);
            peopleForFunction.push(p);
          }
        });
      });
      
      // Also include people who have NO funcoes_habilitadas configured in ANY ministry
      const peopleWithNoFunctionsConfigured = allMinistryPeople.filter(mp => {
        // Check if person has no funcoes_habilitadas in ANY of the selected ministries
        const hasNoFunctionsInAny = ministryIds.some(ministryId => {
          const interesse = mp.ministerios_interesse?.find(i => i.ministerio_id === ministryId);
          const funcHab = interesse?.funcoes_habilitadas;
          return !funcHab || !Array.isArray(funcHab) || funcHab.length === 0;
        });
        const notAlreadyIncluded = !seenIds.has(mp.id);
        return hasNoFunctionsInAny && notAlreadyIncluded;
      });
      
      // Combine both lists
      peopleForFunction = [...peopleForFunction, ...peopleWithNoFunctionsConfigured];
      
      if (peopleSearch.trim()) {
        const search = peopleSearch.toLowerCase();
        peopleForFunction = peopleForFunction.filter(p => 
          p.nome_completo?.toLowerCase().includes(search) ||
          p.email?.toLowerCase().includes(search) ||
          p.telefone?.includes(search)
        );
      }
      
      result[func.id] = peopleForFunction;
    });
    
    return result;
  }, [form.ministerio_id, form.ministerios_ids, peopleSearch, getMultiMinistryFunctions, getPeopleByFunction, getPeopleByMultipleMinistries]);

  // Filtrar músicas pela busca
  const filteredSongs = useMemo(() => {
    if (!songsSearch.trim()) return songs;
    
    const search = songsSearch.toLowerCase();
    return songs.filter(s => 
      s.nome?.toLowerCase().includes(search) ||
      s.autor?.toLowerCase().includes(search) ||
      s.tom?.toLowerCase().includes(search) ||
      s.versao?.toLowerCase().includes(search)
    );
  }, [songs, songsSearch]);

  // Filtrar apenas eventos futuros (que ainda não aconteceram)
  const futureEvents = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return events.filter(event => {
      // Support both old field (data) and new field (data_inicio)
      const dateStr = event.data_inicio || event.data;
      if (!dateStr) return false;
      const eventDate = parseDate(dateStr);
      return eventDate >= today;
    }).sort((a, b) => {
      const dateA = parseDate(a.data_inicio || a.data);
      const dateB = parseDate(b.data_inicio || b.data);
      return dateA - dateB;
    });
  }, [events]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await axios.put(`${API}/schedules/${editingSchedule.id}`, form);
        toast.success("Escala atualizada!");
      } else {
        await axios.post(`${API}/schedules`, form);
        toast.success("Escala criada!");
      }
      setDialogOpen(false);
      setEditingSchedule(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const resetForm = () => {
    setForm({
      nome: "",
      evento_id: "",
      ministerio_id: "",
      ministerios_ids: [],
      pessoas: [],
      musicas: [],
      observacoes: "",
      hora_voluntario_geral: ""
    });
    setShowPeopleSelector(false);
    setShowSongsSelector(false);
    setPeopleSearch("");
    setSongsSearch("");
    setUnavailablePeople([]);
  };

  // Aplicar hora geral a todos os voluntários
  const applyGeneralTimeToAll = (time) => {
    if (!time) return;
    
    const updatedPessoas = form.pessoas.map(p => ({
      ...p,
      hora_voluntario: time
    }));
    
    setForm(prev => ({
      ...prev,
      hora_voluntario_geral: time,
      pessoas: updatedPessoas
    }));
    
    toast.success(`Horário ${time} aplicado a todos os voluntários`);
  };

  // Check unavailability when event is selected
  const checkUnavailability = async (eventId) => {
    if (!eventId) {
      setUnavailablePeople([]);
      return;
    }
    
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    // Support both old field (data) and new field (data_inicio)
    const dateStr = event.data_inicio || event.data;
    if (!dateStr) return;
    const eventDate = dateStr.split("T")[0];
    const personIds = people.map(p => p.id);
    
    try {
      const response = await axios.post(`${API}/schedules/check-availability`, {
        data: eventDate,
        person_ids: personIds
      });
      setUnavailablePeople(response.data.unavailable || []);
    } catch (error) {
      console.log("Error checking availability:", error);
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    const ministryId = schedule.ministerio_id || "";
    setForm({
      nome: schedule.nome,
      evento_id: schedule.evento_id,
      ministerio_id: ministryId,
      ministerios_ids: schedule.ministerios_ids || (ministryId ? [ministryId] : []),
      pessoas: schedule.pessoas || [],
      musicas: schedule.musicas || [],
      observacoes: schedule.observacoes || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover esta escala?")) return;
    try {
      await axios.delete(`${API}/schedules/${id}`);
      toast.success("Escala removida");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [whatsappResults, setWhatsappResults] = useState(null);
  const [sendingPush, setSendingPush] = useState(false);
  const [whatsappOptionsDialog, setWhatsappOptionsDialog] = useState(false);
  const [selectedScheduleForWhatsapp, setSelectedScheduleForWhatsapp] = useState(null);
  const [whatsappSendFilter, setWhatsappSendFilter] = useState("all");
  const [selectedPersonsForWhatsapp, setSelectedPersonsForWhatsapp] = useState([]);
  const [whatsappProgress, setWhatsappProgress] = useState(null);
  const [whatsappProgressDialog, setWhatsappProgressDialog] = useState(false);
  const [whatsappProgressScheduleId, setWhatsappProgressScheduleId] = useState(null);

  const openWhatsappOptions = (schedule) => {
    setSelectedScheduleForWhatsapp(schedule);
    setWhatsappSendFilter("all");
    setSelectedPersonsForWhatsapp([]);
    setWhatsappOptionsDialog(true);
  };

  // Polling para acompanhar progresso do envio
  const pollWhatsappStatus = useCallback(async (scheduleId) => {
    try {
      const res = await axios.get(`${API}/schedules/${scheduleId}/whatsapp-status`);
      setWhatsappProgress(res.data);
      
      if (res.data.status === "enviando") {
        // Continua polling (fila em ritmo humano)
        setTimeout(() => pollWhatsappStatus(scheduleId), 5000);
      } else if (res.data.status === "concluido") {
        // Finalizado
        setSendingWhatsapp(false);
        if (res.data.enviados > 0) {
          toast.success(`${res.data.enviados} mensagem(ns) enviada(s) com sucesso!`);
        }
        if (res.data.erros > 0) {
          toast.error(`${res.data.erros} mensagem(ns) com erro`);
        }
        loadData();
      } else if (res.data.status === "erro") {
        setSendingWhatsapp(false);
        toast.error("Erro no envio de mensagens");
        loadData();
      }
    } catch (error) {
      console.error("Erro ao verificar status:", error);
    }
  }, [loadData]);

  const handleSendWhatsapp = async () => {
    if (!selectedScheduleForWhatsapp) return;
    
    const scheduleId = selectedScheduleForWhatsapp.id;
    setWhatsappProgressScheduleId(scheduleId);
    setSendingWhatsapp(true);
    setWhatsappOptionsDialog(false);
    setWhatsappProgressDialog(true);
    setWhatsappProgress({
      status: "iniciando",
      progresso: 0,
      total: 0,
      enviados: 0,
      erros: 0
    });
    
    try {
      const res = await axios.post(`${API}/schedules/${scheduleId}/send-whatsapp`, {
        filter: whatsappSendFilter,
        person_ids: selectedPersonsForWhatsapp
      });
      
      toast.info(res.data.message);
      
      // Inicia polling para acompanhar progresso
      setTimeout(() => pollWhatsappStatus(scheduleId), 1000);
      
    } catch (error) {
      setSendingWhatsapp(false);
      setWhatsappProgressDialog(false);
      toast.error(error.response?.data?.detail || "Erro ao enviar mensagens");
    } finally {
      setSelectedScheduleForWhatsapp(null);
    }
  };

  const handleResetWhatsappStatus = async (scheduleId) => {
    try {
      await axios.post(`${API}/schedules/${scheduleId}/whatsapp-reset`);
      toast.success("Status resetado com sucesso");
      setWhatsappProgressDialog(false);
      setWhatsappProgress(null);
      loadData();
    } catch (error) {
      toast.error("Erro ao resetar status");
    }
  };

  const togglePersonForWhatsapp = (personId) => {
    setSelectedPersonsForWhatsapp(prev => 
      prev.includes(personId) 
        ? prev.filter(id => id !== personId)
        : [...prev, personId]
    );
  };

  const handleSendPushNotification = async (scheduleId) => {
    if (!window.confirm("Deseja enviar notificação push para todos os escalados?")) return;
    
    setSendingPush(true);
    try {
      const res = await axios.post(`${API}/push/send-schedule-reminder`, {
        schedule_id: scheduleId
      });
      
      if (res.data.notifications_sent > 0) {
        toast.success(`${res.data.notifications_sent} notificação(ões) enviada(s)!`);
      } else {
        toast.info("Nenhum escalado possui notificações ativas");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar notificações");
    } finally {
      setSendingPush(false);
    }
  };

  const togglePerson = async (functionId, functionNome, personId, personNome) => {
    const exists = form.pessoas.find(p => p.person_id === personId && p.function_id === functionId);
    
    if (exists) {
      setForm(prev => ({
        ...prev,
        pessoas: prev.pessoas.filter(p => !(p.person_id === personId && p.function_id === functionId))
      }));
    } else {
      // Check availability before adding
      if (form.event_id) {
        try {
          const event = events.find(e => e.id === form.event_id);
          if (event) {
            const dateStr = event.data_inicio || event.data;
            if (dateStr) {
              const eventDate = dateStr.split("T")[0];
              const response = await axios.get(`${API}/check-availability/${personId}?date=${eventDate}`);
              
              if (!response.data.available) {
                toast.error(`${personNome} não está disponível: ${response.data.reason}`, {
                  description: `Período: ${response.data.periodo}`,
                  duration: 5000
                });
                return;
              }
            }
          }
        } catch (error) {
          console.log("Availability check error:", error);
        }
      }
      
      setForm(prev => ({
        ...prev,
        pessoas: [...prev.pessoas, { 
          person_id: personId, 
          person_nome: personNome, 
          function_id: functionId, 
          function_nome: functionNome, 
          hora_voluntario: prev.hora_voluntario_geral || "" 
        }]
      }));
    }
  };

  const toggleSong = (song) => {
    const exists = form.musicas.find(m => m.song_id === song.id);
    
    if (exists) {
      setForm(prev => ({
        ...prev,
        musicas: prev.musicas.filter(m => m.song_id !== song.id)
      }));
    } else {
      // Check if song has multiple versions
      if (song.versoes && song.versoes.length > 1) {
        setSelectedSongForVersion(song);
        setVersionSelectorOpen(true);
      } else {
        // Get version info (first version or tom field)
        const versionInfo = song.versoes && song.versoes.length > 0 
          ? { versao_nome: song.versoes[0].nome, tom: song.versoes[0].tom }
          : { versao_nome: "", tom: song.tom };
        
        addSongToList(song, versionInfo);
      }
    }
  };

  const addSongToList = (song, versionInfo) => {
    setForm(prev => ({
      ...prev,
      musicas: [...prev.musicas, {
        song_id: song.id,
        song_nome: song.nome,
        tom: versionInfo.tom || "",
        versao_nome: versionInfo.versao_nome || "",
        link_letra: song.link_letra,
        link_video: song.link_video,
        link_cifra: song.link_cifra,
        ordem: prev.musicas.length
      }]
    }));
  };

  const handleVersionSelect = (version) => {
    if (selectedSongForVersion) {
      addSongToList(selectedSongForVersion, { versao_nome: version.nome, tom: version.tom });
      setVersionSelectorOpen(false);
      setSelectedSongForVersion(null);
    }
  };

  const moveSong = (index, direction) => {
    const newMusicas = [...form.musicas];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newMusicas.length) return;
    
    [newMusicas[index], newMusicas[newIndex]] = [newMusicas[newIndex], newMusicas[index]];
    
    // Update ordem
    newMusicas.forEach((m, i) => { m.ordem = i; });
    
    setForm(prev => ({ ...prev, musicas: newMusicas }));
  };

  const isSongSelected = (songId) => {
    return form.musicas.some(m => m.song_id === songId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="escalas-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Escalas</h1>
        <Button onClick={() => { setEditingSchedule(null); resetForm(); setDialogOpen(true); }} data-testid="add-schedule-button">
          <Plus className="h-4 w-4 mr-2" />
          Nova Escala
        </Button>
      </div>

      <div className="space-y-4">
        {schedules.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Nenhuma escala cadastrada</p>
        ) : (
          [...schedules].sort((a, b) => {
            // Separate future and past schedules
            const isPastA = isSchedulePast(a);
            const isPastB = isSchedulePast(b);
            
            // Future schedules come first
            if (!isPastA && isPastB) return -1;
            if (isPastA && !isPastB) return 1;
            
            // Within future: sort by date ascending (next event first)
            // Within past: sort by date descending (most recent first)
            const dateA = a.evento_data ? parseDate(a.evento_data) : new Date(0);
            const dateB = b.evento_data ? parseDate(b.evento_data) : new Date(0);
            
            if (!isPastA && !isPastB) {
              // Future schedules: ascending (soonest first)
              return dateA - dateB;
            } else {
              // Past schedules: descending (most recent first)
              return dateB - dateA;
            }
          }).map((schedule) => {
            const isPast = isSchedulePast(schedule);
            return (
            <Card 
              key={schedule.id} 
              className={`overflow-hidden transition-all ${isPast ? 'opacity-50 bg-muted/30' : ''}`}
              data-testid={`schedule-card-${schedule.id}`}
            >
              <CardContent className="p-4">
                <div 
                  className="flex items-start gap-4 cursor-pointer"
                  onClick={() => setExpandedSchedule(expandedSchedule === schedule.id ? null : schedule.id)}
                >
                  <div className={`flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center ${isPast ? 'bg-gray-200' : 'bg-primary/10'}`}>
                    {schedule.evento_data ? (
                      <>
                        <span className={`text-xs uppercase ${isPast ? 'text-gray-400' : 'text-muted-foreground'}`}>
                          {format(parseDate(schedule.evento_data), "MMM", { locale: ptBR })}
                        </span>
                        <span className={`text-xl font-bold ${isPast ? 'text-gray-400' : 'text-primary'}`}>
                          {format(parseDate(schedule.evento_data), "dd")}
                        </span>
                      </>
                    ) : (
                      <Calendar className={`h-6 w-6 ${isPast ? 'text-gray-400' : 'text-primary'}`} />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{schedule.nome}</h3>
                      {isPast && (
                        <Badge variant="secondary" className="text-xs bg-gray-200 text-gray-500">
                          Passada
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{schedule.evento_nome}</p>
                    
                    <div className="flex items-center gap-3 mt-2">
                      {schedule.evento_horario && (
                        <Badge variant="secondary" className="text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          {schedule.evento_horario}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {schedule.pessoas?.length || 0} pessoas
                      </Badge>
                      {schedule.musicas?.length > 0 && (
                        <Badge className="bg-purple-500/10 text-purple-500 text-xs">
                          <Music className="h-3 w-3 mr-1" />
                          {schedule.musicas.length} músicas
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    {expandedSchedule === schedule.id ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {expandedSchedule === schedule.id && (
                  <div className="mt-4 pt-4 border-t border-border/50 space-y-4">
                    {/* People */}
                    {schedule.pessoas?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Equipe Escalada
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {schedule.pessoas.map((p, idx) => (
                            <Badge 
                              key={idx} 
                              variant={p.confirmado ? "default" : p.whatsapp_enviado ? "secondary" : "outline"}
                              className={
                                p.confirmado 
                                  ? "bg-green-500/20 text-green-700 border-green-500/30" 
                                  : p.whatsapp_enviado 
                                    ? "bg-blue-500/20 text-blue-700 border-blue-500/30" 
                                    : ""
                              }
                            >
                              {p.confirmado && <Check className="h-3 w-3 mr-1" />}
                              {p.person_nome}
                              <span className="text-muted-foreground ml-1">({p.function_nome})</span>
                              {p.confirmado && <span className="ml-1 text-xs">(Confirmado)</span>}
                              {!p.confirmado && p.whatsapp_enviado && <span className="ml-1 text-xs">(Enviado)</span>}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Songs */}
                    {schedule.musicas?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Music className="h-4 w-4" />
                          Músicas
                        </h4>
                        <div className="space-y-2">
                          {schedule.musicas.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground text-sm">{idx + 1}.</span>
                                <span className="font-medium">{m.song_nome}</span>
                                {m.tom && <span className="text-muted-foreground ml-2">Tom: {m.tom}</span>}
                              </div>
                              <div className="flex gap-1">
                                {m.link_letra && (
                                  <a href={m.link_letra} target="_blank" rel="noopener noreferrer">
                                    <Button size="sm" variant="ghost">Letra</Button>
                                  </a>
                                )}
                                {m.link_cifra && (
                                  <a href={m.link_cifra} target="_blank" rel="noopener noreferrer">
                                    <Button size="sm" variant="ghost">Cifra</Button>
                                  </a>
                                )}
                                {m.link_video && (
                                  <a href={m.link_video} target="_blank" rel="noopener noreferrer">
                                    <Button size="sm" variant="ghost">Vídeo</Button>
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Observations */}
                    {schedule.observacoes && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Observações
                        </h4>
                        <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">
                          {schedule.observacoes}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      {!isPast && (
                        <>
                          {schedule.whatsapp_status === "enviando" ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="gap-2 border-green-500 text-green-600"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setSelectedScheduleForWhatsapp(schedule);
                                setWhatsappProgressScheduleId(schedule.id);
                                setWhatsappProgressDialog(true);
                                pollWhatsappStatus(schedule.id);
                              }}
                              data-testid={`whatsapp-progress-${schedule.id}`}
                            >
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Ver Progresso
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="gap-2"
                              onClick={(e) => { e.stopPropagation(); openWhatsappOptions(schedule); }}
                              disabled={sendingWhatsapp}
                              data-testid={`whatsapp-schedule-${schedule.id}`}
                            >
                              {sendingWhatsapp ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <MessageCircle className="h-4 w-4" />
                              )}
                              {sendingWhatsapp ? "Enviando..." : "WhatsApp"}
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-2"
                            onClick={(e) => { e.stopPropagation(); handleSendPushNotification(schedule.id); }}
                            disabled={sendingPush}
                            data-testid={`push-schedule-${schedule.id}`}
                          >
                            {sendingPush ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Bell className="h-4 w-4" />
                            )}
                            {sendingPush ? "Enviando..." : "Push"}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleEdit(schedule); }}
                            data-testid={`edit-schedule-${schedule.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-destructive"
                            onClick={(e) => { e.stopPropagation(); handleDelete(schedule.id); }}
                            data-testid={`delete-schedule-${schedule.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {isPast && (
                        <p className="text-sm text-muted-foreground italic">
                          Esta escala já passou e não pode ser editada
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )})
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] p-0 flex flex-col">
          <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0">
            <DialogTitle>{editingSchedule ? "Editar Escala" : "Nova Escala"}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="flex-1 overflow-y-auto px-4">
            <form id="schedule-form" onSubmit={handleSubmit} className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Nome da Escala *</Label>
                <Input
                  required
                  placeholder="Ex: Escala Domingo Manhã"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  data-testid="schedule-nome"
                />
              </div>

              <div className="space-y-2">
                <Label>Evento *</Label>
                <Select
                  value={form.evento_id}
                  onValueChange={(v) => {
                    setForm({ ...form, evento_id: v });
                    checkUnavailability(v);
                  }}
                >
                  <SelectTrigger data-testid="schedule-evento">
                    <SelectValue placeholder="Selecione o evento" />
                  </SelectTrigger>
                  <SelectContent>
                    {futureEvents.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        Nenhum evento futuro cadastrado
                      </div>
                    ) : (
                      futureEvents.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          <span>{e.titulo || e.nome} - {formatDateBR(e.data_inicio || e.data)} {e.horario}</span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Ministérios *</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Selecione um ou mais ministérios para escalar pessoas
                </p>
                <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {accessibleMinistries.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      Nenhum ministério disponível
                    </p>
                  ) : (
                    accessibleMinistries.map((m) => {
                      const isSelected = form.ministerios_ids?.includes(m.id);
                      return (
                        <label
                          key={m.id}
                          className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'
                          }`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleMinistryToggle(m.id)}
                            data-testid={`ministry-checkbox-${m.id}`}
                          />
                          <span className="flex-1">{m.nome}</span>
                          {m.tipo_louvor && (
                            <Badge variant="secondary" className="text-xs">
                              <Music className="h-3 w-3 mr-1" />
                              Louvor
                            </Badge>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
                {form.ministerios_ids?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {form.ministerios_ids.length} ministério(s) selecionado(s)
                  </p>
                )}
              </div>

              {form.ministerios_ids?.length > 0 && (
                <>
                  <Separator />
                  
                  {/* People Selector - Opens Fullscreen Modal */}
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between h-auto py-3"
                      onClick={() => setPeopleSelectorOpen(true)}
                      data-testid="toggle-people-selector"
                    >
                      <span className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span className="flex flex-col items-start">
                          <span>Selecionar Pessoas</span>
                          <span className="text-xs text-muted-foreground font-normal">
                            {form.pessoas.length} selecionadas
                          </span>
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    
                    {/* Preview of selected people */}
                    {form.pessoas.length > 0 && (
                      <div className="flex flex-wrap gap-1 p-2 bg-muted/30 rounded-lg overflow-hidden">
                        {form.pessoas.slice(0, 3).map((p) => (
                          <Badge key={`${p.person_id}-${p.function_id}`} variant="secondary" className="text-xs truncate max-w-[80px]">
                            {p.person_nome.split(' ')[0]}
                          </Badge>
                        ))}
                        {form.pessoas.length > 3 && (
                          <Badge variant="outline" className="text-xs flex-shrink-0">
                            +{form.pessoas.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Songs Selector - Opens Fullscreen Modal - Only for Worship Ministries */}
                  {form.ministerios_ids?.some(id => isWorshipMinistry(id)) && (
                    <div className="space-y-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between h-auto py-3"
                        onClick={() => setSongsSelectorOpen(true)}
                        data-testid="toggle-songs-selector"
                      >
                        <span className="flex items-center gap-2">
                          <Music className="h-4 w-4" />
                          <span className="flex flex-col items-start">
                            <span>Selecionar Músicas</span>
                            <span className="text-xs text-muted-foreground font-normal">
                              {form.musicas.length} selecionadas
                            </span>
                          </span>
                        </span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>

                      {/* Preview of selected songs with reorder */}
                      {form.musicas.length > 0 && (
                        <div className="border rounded-xl p-2 space-y-1.5 bg-gradient-to-b from-green-50/50 to-green-50/20 dark:from-green-950/20 dark:to-green-950/10 overflow-hidden">
                          <div className="flex items-center justify-between px-1">
                            <h4 className="font-medium text-xs flex items-center gap-1 text-green-700 dark:text-green-400">
                              <Check className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">Ordem de execução</span>
                            </h4>
                            <Badge variant="outline" className="text-[10px] text-green-700 border-green-300 dark:text-green-400 flex-shrink-0 ml-1">
                              {form.musicas.length}
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            {form.musicas.map((m, idx) => (
                              <div 
                                key={m.song_id} 
                                className="flex items-center gap-1 p-1.5 rounded-lg bg-background border text-sm overflow-hidden"
                              >
                                <span className="text-muted-foreground font-mono text-[10px] w-4 text-center flex-shrink-0">{idx + 1}</span>
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <span className="font-medium block truncate text-xs">{m.song_nome}</span>
                                  {m.tom && <Badge variant="outline" className="text-[9px] px-1 py-0 mt-0.5">{m.tom}</Badge>}
                                </div>
                                <div className="flex items-center flex-shrink-0">
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5"
                                    disabled={idx === 0}
                                    onClick={() => moveSong(idx, "up")}
                                  >
                                    <ArrowUp className="h-2.5 w-2.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5"
                                    disabled={idx === form.musicas.length - 1}
                                    onClick={() => moveSong(idx, "down")}
                                  >
                                    <ArrowDown className="h-2.5 w-2.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    className="h-5 w-5 text-destructive hover:text-destructive"
                                    onClick={() => toggleSong({ id: m.song_id })}
                                  >
                                    <Trash2 className="h-2.5 w-2.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Observations */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Observações
                    </Label>
                    <Textarea
                      placeholder="Observações sobre a escala..."
                      value={form.observacoes}
                      onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                      rows={3}
                      data-testid="schedule-observacoes"
                    />
                  </div>
                </>
              )}
            </form>
          </ScrollArea>
          
          {/* Fixed Footer Buttons */}
          <div className="flex gap-2 p-4 border-t bg-background flex-shrink-0">
            <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="schedule-form" className="flex-1" data-testid="save-schedule-button">
              Salvar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Results Dialog */}
      {/* WhatsApp Send Options Dialog */}
      <Dialog open={whatsappOptionsDialog} onOpenChange={setWhatsappOptionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />
              Enviar WhatsApp
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label>Para quem enviar?</Label>
              <RadioGroup 
                value={whatsappSendFilter} 
                onValueChange={(value) => {
                  setWhatsappSendFilter(value);
                  if (value !== "individual") {
                    setSelectedPersonsForWhatsapp([]);
                  }
                }}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all" className="cursor-pointer">
                    Todos os escalados ({selectedScheduleForWhatsapp?.pessoas?.length || 0})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pending" id="pending" />
                  <Label htmlFor="pending" className="cursor-pointer">
                    Apenas não notificados ({selectedScheduleForWhatsapp?.pessoas?.filter(p => !p.whatsapp_enviado)?.length || 0})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="individual" id="individual" />
                  <Label htmlFor="individual" className="cursor-pointer">
                    Selecionar individualmente
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {whatsappSendFilter === "individual" && (
              <div className="space-y-2">
                <Label>Selecione as pessoas:</Label>
                <ScrollArea className="h-48 border rounded-lg p-2">
                  <div className="space-y-2">
                    {selectedScheduleForWhatsapp?.pessoas?.map((pessoa, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-center space-x-2 p-2 rounded hover:bg-muted/50"
                      >
                        <Checkbox
                          id={`person-${idx}`}
                          checked={selectedPersonsForWhatsapp.includes(pessoa.person_id)}
                          onCheckedChange={() => togglePersonForWhatsapp(pessoa.person_id)}
                        />
                        <Label htmlFor={`person-${idx}`} className="cursor-pointer flex-1 flex items-center justify-between">
                          <span>{pessoa.person_nome}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{pessoa.function_nome}</span>
                            {pessoa.whatsapp_enviado && (
                              <Badge variant="outline" className="text-xs text-green-600">
                                <Check className="h-3 w-3 mr-1" />
                                Enviado
                              </Badge>
                            )}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
              <p>Intervalo entre mensagens: 10-20 segundos</p>
              <p className="mt-1">
                {whatsappSendFilter === "all" && `Serão enviadas ${selectedScheduleForWhatsapp?.pessoas?.length || 0} mensagens`}
                {whatsappSendFilter === "pending" && `Serão enviadas ${selectedScheduleForWhatsapp?.pessoas?.filter(p => !p.whatsapp_enviado)?.length || 0} mensagens`}
                {whatsappSendFilter === "individual" && `Serão enviadas ${selectedPersonsForWhatsapp.length} mensagens`}
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWhatsappOptionsDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSendWhatsapp}
              disabled={whatsappSendFilter === "individual" && selectedPersonsForWhatsapp.length === 0}
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={whatsappDialog} onOpenChange={setWhatsappDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />
              Resultado do Envio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {whatsappResults?.summary && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <p className="text-2xl font-bold text-green-500">{whatsappResults.summary.enviados}</p>
                  <p className="text-xs text-muted-foreground">Enviados</p>
                </div>
                <div className="p-2 rounded-lg bg-red-500/10">
                  <p className="text-2xl font-bold text-red-500">{whatsappResults.summary.erros}</p>
                  <p className="text-xs text-muted-foreground">Erros</p>
                </div>
                <div className="p-2 rounded-lg bg-orange-500/10">
                  <p className="text-2xl font-bold text-orange-500">{whatsappResults.summary.sem_telefone}</p>
                  <p className="text-xs text-muted-foreground">Sem Tel.</p>
                </div>
              </div>
            )}
            
            <Separator />
            
            <ScrollArea className="h-64">
              <div className="space-y-2">
                {whatsappResults?.results?.map((result, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      result.success ? "bg-green-500/10" : result.status === "sem_telefone" ? "bg-orange-500/10" : "bg-red-500/10"
                    }`}
                  >
                    <div>
                      <p className="font-medium">{result.nome}</p>
                      <p className="text-xs text-muted-foreground">
                        {result.telefone || "Sem telefone"}
                      </p>
                    </div>
                    <Badge variant={result.success ? "default" : "destructive"}>
                      {result.success ? "Enviado" : result.status === "sem_telefone" ? "Sem Tel." : "Erro"}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
          <Button onClick={() => setWhatsappDialog(false)} className="w-full">
            Fechar
          </Button>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Progress Dialog - Interface Moderna */}
      <Dialog open={whatsappProgressDialog} onOpenChange={(open) => setWhatsappProgressDialog(open)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />
              {whatsappProgress?.status === "enviando" ? "Enviando em segundo plano" : 
               whatsappProgress?.status === "concluido" ? "Envio Concluído" : 
               whatsappProgress?.status === "erro" ? "Erro no Envio" : "Iniciando..."}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Barra de Progresso */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Progresso</span>
                <span className="font-medium">
                  {whatsappProgress?.progresso || 0} / {whatsappProgress?.total || 0}
                </span>
              </div>
              <div className="h-3 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500 ease-out"
                  style={{ 
                    width: whatsappProgress?.total > 0 
                      ? `${(whatsappProgress?.progresso / whatsappProgress?.total) * 100}%` 
                      : '0%' 
                  }}
                />
              </div>
            </div>

            {/* Status atual */}
            {whatsappProgress?.status === "enviando" && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20" data-testid="whatsapp-progress-status">
                <Loader2 className="h-5 w-5 text-blue-500 animate-spin shrink-0" />
                <div className="text-sm min-w-0">
                  {whatsappProgress?.fila_pausada ? (
                    <p className="font-medium text-amber-700">Fila pausada nas configurações do WhatsApp</p>
                  ) : whatsappProgress?.fora_janela ? (
                    <p className="font-medium text-blue-700">Fora do horário de envio · retoma às {whatsappProgress.proxima_janela ? new Date(whatsappProgress.proxima_janela).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "--"}</p>
                  ) : whatsappProgress?.enviando_para ? (
                    <>
                      <p className="font-medium">Próximo: <span className="text-blue-600 dark:text-blue-400">{whatsappProgress.enviando_para}</span></p>
                      {whatsappProgress?.proximo_envio && (
                        <p className="text-xs text-muted-foreground">às {new Date(whatsappProgress.proximo_envio).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</p>
                      )}
                    </>
                  ) : (
                    <p className="font-medium">Aguardando a fila...</p>
                  )}
                  {whatsappProgress?.previsao_fim && (
                    <p className="text-xs text-muted-foreground">Previsão de término: {new Date(whatsappProgress.previsao_fim).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
                  )}
                </div>
              </div>
            )}

            {/* Contadores */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <Check className="h-4 w-4 text-green-500" />
                  <span className="text-xs text-muted-foreground">Enviados</span>
                </div>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {whatsappProgress?.enviados || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-muted-foreground">Erros</span>
                </div>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {whatsappProgress?.erros || 0}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-orange-500/10 to-orange-500/5 border border-orange-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-muted-foreground">Sem Tel.</span>
                </div>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {whatsappProgress?.sem_telefone || 0}
                </p>
              </div>
            </div>

            {/* Mensagem de delay */}
            {whatsappProgress?.status === "enviando" && (
              <div className="text-center text-xs text-muted-foreground bg-emerald-500/10 p-2 rounded-lg" data-testid="whatsapp-background-note">
                🛡️ Modo humano: intervalos aleatórios e pausas por lote para evitar restrição. <strong>Pode fechar esta tela</strong> — o envio continua em segundo plano.
              </div>
            )}

            {/* Resultados finais */}
            {whatsappProgress?.status === "concluido" && whatsappProgress?.results?.length > 0 && (
              <ScrollArea className="h-40">
                <div className="space-y-2">
                  {whatsappProgress.results.map((result, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center justify-between p-2 rounded-lg text-sm ${
                        result.success ? "bg-green-500/10" : result.status === "sem_telefone" ? "bg-orange-500/10" : "bg-red-500/10"
                      }`}
                    >
                      <span className="font-medium truncate">{result.nome}</span>
                      <Badge variant={result.success ? "default" : "destructive"} className="text-xs">
                        {result.success ? "✓" : result.status === "sem_telefone" ? "Sem Tel." : "Erro"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            {whatsappProgress?.status === "enviando" ? (
              <>
                <Button variant="outline" onClick={() => { setWhatsappProgressDialog(false); setSendingWhatsapp(false); loadData(); }} className="flex-1" data-testid="whatsapp-progress-close">
                  Fechar (continua enviando)
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  data-testid="whatsapp-progress-cancel"
                  onClick={async () => {
                    if (!whatsappProgressScheduleId || !window.confirm("Cancelar as mensagens que ainda não foram enviadas?")) return;
                    try {
                      const r = await axios.post(`${API}/schedules/${whatsappProgressScheduleId}/whatsapp-cancel`);
                      toast.success(r.data.message);
                      setSendingWhatsapp(false);
                      pollWhatsappStatus(whatsappProgressScheduleId);
                    } catch (e) { toast.error("Erro ao cancelar"); }
                  }}
                >
                  Cancelar envio
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setWhatsappProgressDialog(false);
                    setWhatsappProgress(null);
                  }} 
                  className="flex-1"
                >
                  Fechar
                </Button>
                {(whatsappProgress?.status === "erro" || 
                  (whatsappProgress?.status === "concluido" && whatsappProgress?.erros > 0)) && (
                  <Button 
                    variant="destructive"
                    onClick={() => selectedScheduleForWhatsapp && handleResetWhatsappStatus(selectedScheduleForWhatsapp.id)}
                    className="flex-1"
                  >
                    Resetar Status
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={versionSelectorOpen} onOpenChange={setVersionSelectorOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-purple-500" />
              Selecione a Versão
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <p className="text-sm text-muted-foreground">
              Música: <span className="font-medium">{selectedSongForVersion?.nome}</span>
            </p>
            <div className="space-y-2">
              {selectedSongForVersion?.versoes?.map((version) => (
                <Button
                  key={version.id}
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => handleVersionSelect(version)}
                >
                  <span>{version.nome}</span>
                  <Badge variant="secondary">Tom: {version.tom}</Badge>
                </Button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fullscreen People Selector Modal */}
      <Dialog open={peopleSelectorOpen} onOpenChange={setPeopleSelectorOpen}>
        <DialogContent className="w-full h-full max-w-full max-h-full sm:max-w-2xl sm:max-h-[90vh] sm:rounded-lg rounded-none p-0 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-background sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setPeopleSelectorOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
              <div>
                <h2 className="font-semibold">Selecionar Pessoas</h2>
                <p className="text-xs text-muted-foreground">{form.pessoas.length} selecionadas</p>
              </div>
            </div>
            <Button 
              size="sm" 
              onClick={() => setPeopleSelectorOpen(false)}
            >
              Confirmar
            </Button>
          </div>

          {/* Search */}
          <div className="px-4 py-3 border-b bg-muted/30">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar pessoa por nome..."
                className="pl-10 bg-background"
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
                data-testid="people-search-modal"
              />
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-6">
              {getMultiMinistryFunctions(form.ministerios_ids?.length > 0 ? form.ministerios_ids : (form.ministerio_id ? [form.ministerio_id] : [])).map((func) => {
                const peopleForFunc = filteredPeopleByFunction[func.id] || [];
                return (
                  <div key={func.id}>
                    <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      {func.nome}
                      <Badge variant="secondary" className="ml-auto text-xs">
                        {peopleForFunc.length} disponíveis
                      </Badge>
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {peopleForFunc.map((person) => {
                        const isSelected = form.pessoas.some(
                          p => p.person_id === person.id && p.function_id === func.id
                        );
                        const isUnavailable = unavailablePeople.some(u => u.person_id === person.id);
                        const unavailInfo = unavailablePeople.find(u => u.person_id === person.id);
                        
                        return (
                          <div
                            key={`${func.id}-${person.id}`}
                            className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all active:scale-[0.98] ${
                              isSelected 
                                ? "bg-primary/10 border-primary" 
                                : isUnavailable 
                                  ? "bg-destructive/5 border-destructive/30 opacity-70" 
                                  : "bg-background hover:bg-muted border-border"
                            }`}
                            onClick={() => togglePerson(func.id, func.nome, person.id, person.nome_completo)}
                          >
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                            }`}>
                              {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`font-medium block ${isSelected ? 'text-primary' : ''}`}>
                                {person.nome_completo}
                              </span>
                              {isUnavailable && (
                                <span className="text-xs text-destructive">
                                  Indisponível: {unavailInfo?.motivo || ""}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {peopleForFunc.length === 0 && (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                          {peopleSearch ? "Nenhum resultado encontrado" : "Nenhum voluntário aprovado"}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              {getMultiMinistryFunctions(form.ministerios_ids?.length > 0 ? form.ministerios_ids : (form.ministerio_id ? [form.ministerio_id] : [])).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Nenhuma função cadastrada para os ministérios selecionados
                </p>
              )}

              {/* Horário dos Voluntários */}
              {form.pessoas.length > 0 && (
                <div className="pt-4 border-t">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" />
                    Horário de Chegada
                  </h4>
                  
                  {/* Campo Geral */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20 mb-4">
                    <div className="flex-1">
                      <span className="font-medium text-sm">Hora Geral</span>
                      <p className="text-muted-foreground text-xs">Aplica para todos</p>
                    </div>
                    <Input
                      type="time"
                      className="w-24"
                      value={form.hora_voluntario_geral || ""}
                      onChange={(e) => setForm(prev => ({ ...prev, hora_voluntario_geral: e.target.value }))}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => applyGeneralTimeToAll(form.hora_voluntario_geral)}
                      disabled={!form.hora_voluntario_geral}
                    >
                      Aplicar
                    </Button>
                  </div>
                  
                  {/* Horários Individuais */}
                  <div className="space-y-2">
                    {form.pessoas.map((p, idx) => (
                      <div key={`${p.person_id}-${p.function_id}`} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium text-sm truncate block">{p.person_nome}</span>
                          <span className="text-muted-foreground text-xs">{p.function_nome}</span>
                        </div>
                        <Input
                          type="time"
                          className="w-24"
                          value={p.hora_voluntario || ""}
                          onChange={(e) => {
                            const newPessoas = [...form.pessoas];
                            newPessoas[idx] = { ...newPessoas[idx], hora_voluntario: e.target.value };
                            setForm(prev => ({ ...prev, pessoas: newPessoas }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Songs Selector Modal */}
      <Dialog open={songsSelectorOpen} onOpenChange={setSongsSelectorOpen}>
        <DialogContent className="w-full h-full max-w-full max-h-full sm:max-w-2xl sm:max-h-[90vh] sm:rounded-lg rounded-none p-0 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-background sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => setSongsSelectorOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
              <div>
                <h2 className="font-semibold">Selecionar Músicas</h2>
                <p className="text-xs text-muted-foreground">{form.musicas.length} selecionadas</p>
              </div>
            </div>
            <Button 
              size="sm" 
              onClick={() => setSongsSelectorOpen(false)}
            >
              Confirmar
            </Button>
          </div>

          {/* Search */}
          <div className="px-4 py-3 border-b bg-muted/30">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar música por nome, autor, tom..."
                className="pl-10 bg-background"
                value={songsSearch}
                onChange={(e) => setSongsSearch(e.target.value)}
                data-testid="songs-search-modal"
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <Badge variant="secondary" className="text-xs">
                {filteredSongs.length} músicas disponíveis
              </Badge>
              {form.musicas.length > 0 && (
                <Badge variant="default" className="text-xs bg-green-600">
                  {form.musicas.length} selecionadas
                </Badge>
              )}
            </div>
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            <div className="p-4 space-y-2">
              {filteredSongs.map((song) => {
                const isSelected = isSongSelected(song.id);
                const hasVersions = song.versoes && song.versoes.length > 1;
                const primaryTom = song.versoes && song.versoes.length > 0 
                  ? song.versoes[0].tom 
                  : song.tom;
                return (
                  <div
                    key={song.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all active:scale-[0.98] ${
                      isSelected 
                        ? "bg-primary/10 border-primary shadow-sm" 
                        : "bg-background hover:bg-muted border-border"
                    }`}
                    onClick={() => toggleSong(song)}
                    data-testid={`song-modal-${song.id}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`font-medium block ${isSelected ? 'text-primary' : ''}`}>
                        {song.nome}
                      </span>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                        {song.autor && <span>{song.autor}</span>}
                        {primaryTom && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            {primaryTom}
                          </Badge>
                        )}
                        {hasVersions && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            {song.versoes.length} versões
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredSongs.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {songsSearch ? "Nenhuma música encontrada" : "Nenhuma música cadastrada"}
                </p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
